import { useState } from 'react'
import type { Friend } from '@/types'

const STATUS_RING: Record<string, string> = {
  authed:  'oklch(0.78 0.18 158)',
  public:  'oklch(0.78 0.16 88)',
  limited: 'oklch(0.62 0.04 260)',
}

const STATUS_LABEL: Record<string, string> = {
  authed:  'Logged in',
  public:  'Public profile',
  limited: 'Private profile',
}

// Steam's default "black square with checkmark" avatar hash
const DEFAULT_STEAM_AVATAR_HASH = 'fef49e7fa7e1997310d705b2a6158ff8dc1cdfeb'

interface AvatarProps {
  friend: Friend
  size?: number
  owns?: boolean | null
  showRing?: boolean
}

export function Avatar({ friend, size = 28, owns = null, showRing = true }: AvatarProps) {
  const [imgError, setImgError] = useState(false)

  const isDefaultAvatar = friend.avatarUrl?.includes(DEFAULT_STEAM_AVATAR_HASH)
  const name = friend.nickname || friend.name || '?'
  const initials = name.trim()
    ? name
        .split(' ')
        .filter(Boolean)
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '?'

  const hue = Number(friend.hue) || 200
  const ring = STATUS_RING[friend.status] || 'oklch(0.48 0.02 236)'
  const ghosted = owns === false

  const title = [
    `${name} · ${STATUS_LABEL[friend.status] || 'Unknown'}`,
    owns === true ? '· owns' : owns === false ? "· doesn't own" : '',
  ]
    .filter(Boolean)
    .join(' ')

  // Rich background style
  const bgMain = `oklch(0.55 0.15 ${hue})`
  const bgGradient = `radial-gradient(circle at 72% 24%, oklch(0.84 0.16 ${hue} / 0.4), transparent 24%), linear-gradient(135deg, oklch(0.55 0.15 ${hue}), oklch(0.35 0.10 ${hue}))`

  return (
    <div
      className="cg-avatar"
      data-status={friend.status}
      data-owns={owns}
      title={title}
      style={{
        width: size,
        height: size,
        backgroundColor: bgMain,
        background: bgGradient,
        boxShadow: showRing
          ? `0 0 0 1.5px var(--bg-2), 0 0 0 ${ghosted ? 2 : 3}px ${ring}`
          : 'none',
        opacity: ghosted ? 0.32 : 1,
        filter: ghosted ? 'grayscale(0.6)' : 'none',
        fontSize: size * 0.4,
      }}
    >
      {/* Decorative shapes for depth */}
      <span className="cg-avatar-shape cg-avatar-shape-a" />
      <span className="cg-avatar-shape cg-avatar-shape-b" />

      <span className="cg-avatar-initials" style={{ position: 'relative', zIndex: 1 }}>
        {initials}
      </span>

      {friend.avatarUrl && !isDefaultAvatar && !imgError && (
        <img
          src={friend.avatarUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setImgError(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 2 }}
        />
      )}
      {friend.status === 'limited' && <span className="cg-avatar-lock">🔒</span>}
    </div>
  )
}

export { STATUS_RING, STATUS_LABEL }
